import React, { useState } from "react";

import BoringAvatar from "boring-avatars";
import { twJoin } from "tailwind-merge";

import { Skeleton } from "src/design-system/Skeleton";
import { tailwindSizeToPixels } from "src/utils/tailwindcss";
import { getUserName } from "src/utils/user";

export const AVATAR_FALLBACK_COLORS = [
  "#5946E5",
  "#6366F1",
  "#A7F3D0",
  "#22D3EE",
  "#493FC1",
];

export type AvatarSize = "xs" | "sm" | "lg" | "xl";

const AVATAR_SIZE_MAP = {
  xs: { width: "w-4", height: "h-4" },
  sm: { width: "w-4.5", height: "h-4.5" },
  lg: { width: "w-8", height: "h-8" },
  xl: { width: "w-32", height: "h-32" },
} as const;

type AvatarUser = {
  full_name?: string;
  avatar_url?: string;
  username?: string;
};

type AvatarProps =
  | {
      user: AvatarUser;
      size?: AvatarSize;
      isLoading?: false;
      dataLoc?: string;
    }
  | {
      isLoading: true;
      size?: AvatarSize;
      user?: AvatarUser;
      dataLoc?: string;
    };

export const Avatar: React.FC<AvatarProps> = (props) => {
  const [isFailed, setIsFailed] = useState(false);
  const sizeInfo = AVATAR_SIZE_MAP[props.size ?? "sm"];

  if (props.isLoading) {
    return (
      <div className="shrink-0">
        <Skeleton
          dataLoc={props.dataLoc}
          height={sizeInfo.height}
          rounded="rounded-full"
          width={sizeInfo.width}
        />
      </div>
    );
  }

  const userName = getUserName(props.user);
  const initialLetter = (userName ?? "").substring(0, 1);

  if (props.user.avatar_url && !isFailed) {
    return (
      <div
        className={twJoin(
          "flex shrink-0 justify-center overflow-hidden rounded-full",
          sizeInfo.width,
          sizeInfo.height,
        )}
        data-loc={props.dataLoc}
      >
        <img
          alt={userName}
          className="object-cover"
          src={props.user.avatar_url}
          onError={() => setIsFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      aria-label={userName}
      className={twJoin(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        sizeInfo.width,
        sizeInfo.height,
      )}
      data-loc={props.dataLoc}
      role="img"
    >
      <BoringAvatar
        colors={AVATAR_FALLBACK_COLORS}
        name={userName}
        size={tailwindSizeToPixels(sizeInfo.width)}
        variant="marble"
      />
      <span
        aria-hidden="true"
        className={twJoin(
          "absolute text-shadow-sm text-white uppercase",
          (!props.size || props.size === "xs") && "font-inter-medium-12px",
          props.size === "sm" && "font-inter-medium-12px",
          props.size === "lg" && "font-inter-semibold-16px",
          // scale-[3] is intentional — no design token exists for this size
          props.size === "xl" && "scale-[3] font-inter-semibold-16px",
        )}
      >
        {initialLetter}
      </span>
    </div>
  );
};
